import React from 'react';
import Icon from '../../../ui/Icon';
import ProgressBar from '../../../ui/ProgressBar';

const HUNGER_EFFECT_META = Object.freeze({
  well_fed: { label: 'Well fed', shortLabel: 'Full' },
  fed: { label: 'Fed', shortLabel: 'Fed' },
  hungry: { label: 'Hungry', shortLabel: 'Low' },
  starving: { label: 'Starving', shortLabel: 'Empty' },
});

function formatMultiplier(multiplier, label) {
  const numeric = Number(multiplier);
  if (!Number.isFinite(numeric)) return `${label} modifier active`;
  const percent = Number(((numeric - 1) * 100).toFixed(2));
  if (percent === 0) return `No ${label} modifier`;
  return `${percent > 0 ? '+' : ''}${percent}% ${label}`;
}

export function hungerEffectPresentation(player) {
  const state = HUNGER_EFFECT_META[player?.hungerState]
    ? player.hungerState
    : 'well_fed';
  const meta = HUNGER_EFFECT_META[state];
  const effects = player?.hungerEffects;
  if (!effects) return null;

  if (state === 'well_fed') {
    return {
      ...meta,
      lines: [
        formatMultiplier(
          effects.wellFedRegenMultiplier,
          'HP regeneration',
        ),
      ],
      state,
    };
  }
  if (state === 'hungry') {
    return {
      ...meta,
      lines: [
        formatMultiplier(
          effects.hungryRegenMultiplier,
          'HP regeneration',
        ),
        formatMultiplier(
          effects.hungryDamageMultiplier,
          'outgoing damage',
        ),
      ],
      state,
    };
  }
  if (state === 'starving') {
    const starvationDamage = Number(
      effects.starvationDamagePercentPerMinute,
    );
    const starvationLines = Number.isFinite(starvationDamage)
      && starvationDamage > 0
      ? [
        `-${Number(starvationDamage.toFixed(2))}% max HP per active minute`,
        'Hunger cannot reduce HP below 1',
      ]
      : ['No starvation HP damage'];
    return {
      ...meta,
      lines: [
        formatMultiplier(
          effects.starvingRegenMultiplier,
          'HP regeneration',
        ),
        formatMultiplier(
          effects.starvingDamageMultiplier,
          'outgoing damage',
        ),
        ...starvationLines,
      ],
      state,
    };
  }
  return {
    ...meta,
    lines: ['No hunger modifiers'],
    state,
  };
}

export function HungerStatusEffect({ player }) {
  const presentation = hungerEffectPresentation(player);
  if (!presentation) return null;

  return (
    <aside
      aria-label="Hunger effect"
      className="hunger-effect-hud"
      data-floating-window-obstacle="true"
    >
      <div
        aria-label={[
          presentation.label,
          ...presentation.lines,
        ].join('. ')}
        className="hunger-effect-hud__badge"
        data-state={presentation.state}
        role="img"
        tabIndex="0"
      >
        <Icon name="food" size={22} />
        <span aria-hidden="true" className="hunger-effect-hud__state">
          {presentation.shortLabel}
        </span>
      </div>
      <div
        aria-hidden="true"
        className="hunger-effect-hud__tooltip"
      >
        <strong>{presentation.label}</strong>
        <ul>
          {presentation.lines.map(line => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </div>
    </aside>
  );
}

export function OperativeStatusButton({
  active = false,
  actionLabel = 'Operative Status',
  ariaControls = 'playerInfo-window',
  className = '',
  currentHpPercentage,
  level,
  onClick,
  player,
  toggle = true,
  userMaxHp,
  xpForNextLevel,
  xpProgress,
}) {
  const playerName = player?.name || 'Operative';
  const currentHp = Math.round(Number(player?.currentHp) || 0);
  const maxHp = Math.round(Number(userMaxHp) || 0);
  const currentXp = Math.round(Number(player?.xp) || 0);
  const nextLevelXp = Math.round(Number(xpForNextLevel) || 0);
  const playerLevel = Math.max(1, Math.round(Number(level) || 1));
  const satiety = Math.min(
    100,
    Math.max(0, Number(player?.satiety ?? 100) || 0),
  );
  const hungerState = player?.hungerState || 'well_fed';
  const hungerLabel = {
    well_fed: 'well fed',
    fed: 'fed',
    hungry: 'hungry',
    starving: 'starving',
  }[hungerState] || 'fed';
  const actionVerb = toggle
    ? (active ? 'Close' : 'Open')
    : 'Show';

  return (
    <button
      aria-controls={ariaControls}
      aria-expanded={toggle ? active : undefined}
      aria-label={`${actionVerb} ${actionLabel} for ${playerName}, level ${playerLevel}. Health ${currentHp} of ${maxHp}. Experience ${currentXp} of ${nextLevelXp}. Satiety ${Math.round(satiety)} percent, ${hungerLabel}.`}
      aria-pressed={!toggle ? active : undefined}
      className={[
        'pl-status-bar__section',
        'pl-status-bar__section--operative',
        className,
      ].filter(Boolean).join(' ')}
      onClick={onClick}
      title={`${actionVerb} ${actionLabel}`}
      type="button"
    >
      <Icon name="operative" size={20} />
      <span className="pl-status-bar__operative-content">
        <span className="pl-status-bar__label">{playerName} ({playerLevel})</span>
        <span className="pl-status-bar__meters">
          <span className="pl-status-bar__meter">
            <span className="pl-status-bar__meter-label">HP</span>
            <span className="pl-status-bar__meter-bar">
              <ProgressBar as="span" decorative label="Operative health" value={currentHpPercentage} variant="hp" />
            </span>
            <span className="pl-status-bar__meter-value">{currentHp}/{maxHp}</span>
          </span>
          <span className="pl-status-bar__meter">
            <span className="pl-status-bar__meter-label">XP</span>
            <span className="pl-status-bar__meter-bar">
              <ProgressBar as="span" decorative label="Operative experience" value={xpProgress} variant="xp" />
            </span>
            <span className="pl-status-bar__meter-value">{currentXp}/{nextLevelXp}</span>
          </span>
          <span className="pl-status-bar__meter">
            <span className="pl-status-bar__meter-label">Food</span>
            <span className="pl-status-bar__meter-bar">
              <ProgressBar
                as="span"
                decorative
                label="Operative satiety"
                value={satiety}
                variant={`hunger-${hungerState}`}
              />
            </span>
            <span className="pl-status-bar__meter-value">{Math.round(satiety)}%</span>
          </span>
        </span>
      </span>
    </button>
  );
}

export default function StatusBar({
  currentHpPercentage,
  level,
  onToggleOperative,
  onToggleRegion,
  operativeOpen = false,
  player,
  region,
  regionOpen = false,
  userMaxHp,
  xpForNextLevel,
  xpProgress,
}) {
  const regionName = region?.name || 'Unknown Region';

  return (
    <div
      aria-label="Operative and region status"
      className="pl-status-bar"
      data-floating-window-obstacle="true"
      role="group"
    >
      <OperativeStatusButton
        active={operativeOpen}
        currentHpPercentage={currentHpPercentage}
        level={level}
        onClick={onToggleOperative}
        player={player}
        userMaxHp={userMaxHp}
        xpForNextLevel={xpForNextLevel}
        xpProgress={xpProgress}
      />
      <button
        aria-controls="regionInfo-window"
        aria-expanded={regionOpen}
        aria-label={`${regionOpen ? 'Close' : 'Open'} Region Intel for ${regionName}`}
        className="pl-status-bar__section pl-status-bar__section--region"
        onClick={onToggleRegion}
        title={`${regionOpen ? 'Close' : 'Open'} Region Intel`}
        type="button"
      >
        <Icon name="region" size={20} />
        <span className="pl-status-bar__location">
          <span className="pl-status-bar__eyebrow">Current location</span>
          <span className="pl-status-bar__label">{regionName}</span>
        </span>
      </button>
    </div>
  );
}
